import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string; // YYYY-MM-DD
  enabled?: boolean;
};

export type CashInflowDayRow = {
  inflow_count: number;
  inflow_total: number;
  currency: string | null;
};

export type CashInflowsMatrix = {
  dateKeys: string[]; // YYYY-MM-DD
  byAccount: Map<string, Map<string, CashInflowDayRow>>; // accountId -> dayKey -> row
};

const toNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

function buildDateKeys(dateFrom: string, dateTo: string): string[] {
  const start = new Date(`${dateFrom}T00:00:00Z`);
  const end = new Date(`${dateTo}T00:00:00Z`);
  const keys: string[] = [];

  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    keys.push(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`);
  }

  return keys;
}

function emptyDayRow(): CashInflowDayRow {
  return { inflow_count: 0, inflow_total: 0, currency: null };
}

export function useCashInflowsMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo, enabled = true } = args;

  return useQuery({
    queryKey: ["cash_inflows_matrix", bankAccountIds, dateFrom, dateTo],
    enabled: enabled && bankAccountIds.length > 0 && !!dateFrom && !!dateTo,
    queryFn: async (): Promise<CashInflowsMatrix> => {
      const dateKeys = buildDateKeys(dateFrom, dateTo);

      const { data: rows, error } = await supabase
        .from("cash_inflows" as any)
        .select("bank_account_id,inflow_date,amount,currency")
        .in("bank_account_id", bankAccountIds)
        .gte("inflow_date", dateFrom)
        .lte("inflow_date", dateTo)
        .order("bank_account_id", { ascending: true })
        .order("inflow_date", { ascending: true });

      if (error) throw error;

      const byAccount = new Map<string, Map<string, CashInflowDayRow>>();
      for (const id of bankAccountIds) {
        byAccount.set(id, new Map());
      }

      for (const e of (rows ?? []) as any[]) {
        const accId = String(e.bank_account_id);
        const dayKey = String(e.inflow_date);
        const cur = (e.currency ?? null) as string | null;

        const accMap = byAccount.get(accId) ?? new Map<string, CashInflowDayRow>();
        const row = accMap.get(dayKey) ?? emptyDayRow();

        row.inflow_count += 1;
        row.inflow_total += toNum(e.amount) ?? 0;

        if (!row.currency && cur) {
          row.currency = cur;
        }

        accMap.set(dayKey, row);
        byAccount.set(accId, accMap);
      }

      return { dateKeys, byAccount };
    },
    staleTime: 15_000,
  });
}
